import type { ReactNode } from 'react'

import { type Address, ChainId } from '@morpho-org/blue-sdk'
import { ExternalLink } from '@morpho-org/ui-v6/server'

export interface RiskDisclosure {
  disclosure?: ReactNode
}

export interface RiskDisclosuresMapping {
  [address: Address]: RiskDisclosure
}

const gauntletDisclosure = (
  <div>
    <p>
      Gauntlet Curated Vaults are designed to optimize for risk-adjusted yield, programmatically
      allocating against liquid collateral that has cleared our due diligence, inside supply caps
      set by our risk models. Changes that raise exposure run through the vault&apos;s timelock, so
      you have notice before they take effect. Changes that cut exposure take effect at once. Yield
      tracks what borrowers pay for the liquidity, so it moves with demand and can fall to zero.
    </p>
    <br />
    <p>
      None of this removes risk. You may lose some or all of the assets you supply. In a severe
      move, collateral prices can fall faster than liquidators clear the positions behind them, and
      the shortfall lands in the vault as bad debt. Smart contracts can carry faults. Oracles can
      fail or be manipulated. When utilization runs high, withdrawal waits until borrowers repay or
      new liquidity arrives.
    </p>
    <br />
    <p>
      Our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://vaultbook.gauntlet.xyz/vaults/morpho-vaults/curation-methodology-and-risk-factor-overview">
          curation methodology and risk factor overview
        </ExternalLink>
      </span>{' '}
      sets out how we assess each of these risks and what we do about them. Our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://vaultbook.gauntlet.xyz/">VaultBook</ExternalLink>
      </span>{' '}
      product pages document the vaults we curate. Each user confirms they have read and understood
      the{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://storage.googleapis.com/gauntlet-public-web-assets/gauntlet-branded-vault-disclaimer-dec-2025.pdf">
          Gauntlet Vault Disclaimer
        </ExternalLink>
      </span>
      .
    </p>
    <br />
    <p>Gauntlet vaults are curated by Gauntlet Networks, Inc.</p>
  </div>
)

const gauntletCircleGuardedDisclosure = (
  <div>
    <p>
      Gauntlet Curated Vaults are designed to optimize for risk-adjusted yield, programmatically
      allocating against liquid collateral that has cleared our due diligence, inside supply caps
      set by our risk models. Changes that raise exposure run through the vault&apos;s timelock, so
      you have notice before they take effect. Changes that cut exposure take effect at once. Yield
      tracks what borrowers pay for the liquidity, so it moves with demand and can fall to zero.
    </p>
    <br />
    <p>
      None of this removes risk. You may lose some or all of the assets you supply. In a severe
      move, collateral prices can fall faster than liquidators clear the positions behind them, and
      the shortfall lands in the vault as bad debt. Smart contracts can carry faults. Oracles can
      fail or be manipulated. When utilization runs high, withdrawal waits until borrowers repay or
      new liquidity arrives.
    </p>
    <br />
    <p>
      Our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://vaultbook.gauntlet.xyz/vaults/morpho-vaults/curation-methodology-and-risk-factor-overview">
          curation methodology and risk factor overview
        </ExternalLink>
      </span>{' '}
      sets out how we assess each of these risks and what we do about them. Our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://vaultbook.gauntlet.xyz/">VaultBook</ExternalLink>
      </span>{' '}
      product pages document the vaults we curate. Use of this vault is governed by the{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://storage.googleapis.com/gauntlet-public-web-assets/circle-guarded-vaults-terms-of-use.pdf">
          Circle Guarded Vaults Terms of Use
        </ExternalLink>
      </span>
      . Each user confirms they have read and understood the{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://storage.googleapis.com/gauntlet-public-web-assets/gauntlet-branded-vault-disclaimer-dec-2025.pdf">
          Gauntlet Vault Disclaimer
        </ExternalLink>
      </span>
      .
    </p>
    <br />
    <p>Gauntlet vaults are curated by Gauntlet Networks, Inc.</p>
  </div>
)

const clearstarDisclosure = (
  <div>
    <p>
      Clearstar Vaults are designed to help users earn sustainable, risk-adjusted yield through
      curated exposure to on-chain assets. Each listed collateral asset undergoes a due diligence
      process, where we assess the credibility, transparency, and operational integrity of the
      underlying teams. We also maintain open communication channels with these issuers to stay
      informed about updates, governance changes, or material shifts in protocol design or risk
      exposures.
    </p>
    <br />
    <p>
      While these processes aim to reduce exposure to unforeseen events, users should recognize that
      participation in DeFi carries inherent risks. Smart contract vulnerabilities, market
      dislocations, or liquidity shocks may still occur despite thorough assessment. Moreover, we
      actively monitor secondary and third-order dependencies across protocols to mitigate
      correlated risk, yet these dynamics cannot be fully eliminated while pursuing moderately high
      yields.
    </p>
    <br />
    <p>
      Clearstar Vaults are built for transparency and prudence, striving to maximize performance
      with responsible risk management. We encourage depositors to review vault documentation,
      understand the associated risks, and allocate capital according to their individual risk
      tolerance and investment objectives.
    </p>
  </div>
)

const kpkDisclosure = (
  <div>
    <p>
      For details on how kpk assesses and manages risk, see our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://docs.kpk.io/vaults">Curation Handbook</ExternalLink>
      </span>
      , which outlines the frameworks and safeguards applied across all curated vaults.
    </p>
    <br />
    <p>
      Vaults curated by kpk are experimental, non-custodial smart contracts and may carry
      significant risks, including the possible loss of all assets. kpk does not provide investment
      advice, does not custody your assets, and is not liable for any losses. By using this Vault,
      you confirm that you are not a prohibited person and that you act in compliance with
      applicable laws. Read full disclaimer here:{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://docs.kpk.io/vaults/resources/legal/morpho-disclaimer">
          Disclaimer
        </ExternalLink>
      </span>
      .
    </p>
  </div>
)

const galaxyDisclosureContent = (
  <>
    <p>
      By depositing into, withdrawing from, or otherwise interacting with this vault, you agree to
      be bound by the terms of use set out at{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://docs.curation.galaxy.com/legal/terms-of-use">
          docs.curation.galaxy.com/legal/terms-of-use
        </ExternalLink>
      </span>{' '}
      (“Terms of Use”) and further acknowledge and agree:
    </p>
    <br />
    <p>
      • The vault curator, Galaxy Curation LLC (“Galaxy”) and its affiliates do not in any way
      guarantee the performance, security or availability of the vault or any related software or
      technology, including but not limited to, smart contracts, blockchain networks, centralized
      and decentralized protocols, or any integrations, dependencies, or third-party services.
      <br />• You assume all risks associated with any investment decisions you make in connection
      with any vault and represent that you have conducted, or will conduct, your own independent
      evaluation of the vault, including by regularly reviewing the relevant source code, onchain
      configuration, and underlying exposures no less frequently than every seven (7) days.
      <br />• You are solely responsible for determining whether the vault is appropriate for your
      use, financial circumstances, risk tolerance and technical capabilities.
      <br />• Galaxy is an information and technology service provider only, and does not provide
      investment management, asset management, investment advisory, fiduciary or custodial services
      in connection with any assets you deposit in the vault, and Galaxy does not manage or hold any
      such assets.
    </p>
    <br />
    <p>
      The Terms of Use contain a binding arbitration agreement and a class action waiver. This
      affects your legal rights, including your right to sue in court or to have a jury trial, and
      your right to participate in a class or representative proceeding. Please review Section 13
      (Dispute Resolution) of the Terms of Use carefully prior to depositing into, withdrawing from,
      or otherwise interacting with this vault.
    </p>
  </>
)

const galaxyDisclosure = <div>{galaxyDisclosureContent}</div>

const galaxyCircleGuardedDisclosure = (
  <div>
    {galaxyDisclosureContent}
    <br />
    <p>
      Circle Technology Services, LLC holds the Sentinel role for this vault. By depositing into,
      withdrawing from, or otherwise interacting with this vault, you also agree to be bound by the{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://circle.com/legal/guarded-vaults">
          circle.com/legal/guarded-vaults
        </ExternalLink>
      </span>
    </p>
  </div>
)

const galaxyRobinhoodStockTokensDisclosure = (
  <div>
    {galaxyDisclosureContent}
    <br />
    <p>
      The Galaxy USDG Robinhood Stock Tokens Vault (the “Vault”) is offered and operated solely by
      Galaxy Curation LLC and is not sponsored, endorsed, approved, or otherwise associated with
      Robinhood Markets, Inc. or any of its subsidiaries or affiliates (together, “Robinhood”).
      Robinhood has not reviewed or approved the Vault and has no responsibility or liability for
      it. “Robinhood” and related marks belong to their respective owners and are used here only to
      identify those tokens and that network.
    </p>
    <br />
    <p>
      The Vault interacts with Robinhood stock tokens deployed on Robinhood Chain. We do not issue,
      control, or guarantee these tokens, whose terms are set by their issuer. Using the Vault does
      not make you a Robinhood customer or give you any rights against Robinhood.
    </p>
  </div>
)

const re7Disclosure = (
  <div>
    <p>Risk Warning:</p>
    <br />
    <p>
      Re7 Labs&apos; vaults are designed to allocate capital across on-chain lending markets
      according to predefined parameters and risk frameworks. While these vaults may target
      established protocols and liquid assets, no strategy eliminates risk entirely. Extraordinary
      market events, smart contract vulnerabilities, oracle failures, liquidity constraints,
      malicious attacks, or other technical and market risks may result in partial or total loss of
      funds.
    </p>
    <br />
    <p>
      Vault performance depends on underlying third-party protocols and infrastructure, including
      but not limited to Morpho vaults, markets &amp; protocol, price oracles, and other smart
      contracts. These components operate independently of Re7 Labs and may introduce risks outside
      of Re7 Labs&apos; control.
    </p>
    <br />
    <p>
      Re7 Labs does not custody user assets, execute transactions on behalf of users, or provide
      investment advice or recommendation, and does not guarantee performance, liquidity, or capital
      preservation. All deposits and withdrawals are executed directly by users through smart
      contracts; Re7 Labs does not exercise discretionary control over user assets.
    </p>
    <br />
    <p>
      Participation in vaults involves risks inherent to decentralized finance, including but not
      limited to smart contract risk, counterparty risk, market volatility, and risks arising from
      permissionless interactions with vault contracts.
    </p>
    <br />
    <p>
      Participants should carefully review the{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://re7labs.xyz/terms">Re7 Labs Terms and Conditions</ExternalLink>
      </span>{' '}
      before depositing. By participating, users acknowledge that they understand and accept the
      risks described therein and assume full responsibility for their participation.
    </p>
  </div>
)

const hyperithmSaturnDisclosure = (
  <div>
    <p>
      The underlying markets are conservatively parameterized to handle collateral volatility, but
      some risk remains and loss of principal is possible. sUSDat&apos;s exchange rate is derived
      from the value of STRC (Strategy Inc. perpetual preferred equity) holdings, meaning collateral
      value can fluctuate with STRC market conditions. A sharp decline in STRC price would directly
      reduce sUSDat collateral value and may impair liquidation execution. Other risks, including
      those related to smart contracts and oracles, also apply, so please review carefully before
      depositing.
    </p>
  </div>
)

const steakhousePrimeDisclosure = (
  <div>
    <p>
      Steakhouse (incl. Prime) Vaults are designed to minimize volatility by programmatically
      allocating against highly-liquid assets. While Steakhouse Vaults are focused on established
      markets, no strategy eliminates risk entirely: extraordinary market events or tail risks may
      still drive sudden price movements and volatility in the underlying Morpho markets.
    </p>
    <p>
      We encourage participants to review our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/risk-management">
          Risk Documentation
        </ExternalLink>
      </span>{' '}
      for a detailed explanation of how we assess market volatility and counterparty risk, and
      maintain robust safeguards. Further details on vault structures can be found on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/products">
          Products page
        </ExternalLink>
      </span>
      . Each depositor acknowledges having read and understood our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/documents/disclaimers/vaults">
          Disclaimers
        </ExternalLink>
      </span>
      . All Steakhouse Vaults are curated and operated by Carniceria Tropical SA.
    </p>
  </div>
)

const steakhouseHighYieldDisclosure = (
  <div>
    <p>
      Steakhouse High Yield (incl. Smokehouse) Vaults are designed to provide higher risk-adjusted
      returns through exposure to a wider range of collateral. This approach may expose depositors
      to greater volatility or liquidity constraints. Although ongoing monitoring and dynamic asset
      allocation aims to mitigate large drawdowns, rapid shifts in market sentiment could still
      trigger liquidations or short-lived liquidity disruptions.
    </p>
    <p>
      We encourage participants to review our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/risk-management">
          Risk Documentation
        </ExternalLink>
      </span>{' '}
      for a detailed explanation of how we assess market volatility and counterparty risk, and
      maintain robust safeguards. Further details on vault structures can be found on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/products">
          Products page
        </ExternalLink>
      </span>
      . Each depositor acknowledges having read and understood our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/documents/disclaimers/vaults">
          Disclaimers
        </ExternalLink>
      </span>
      . All Steakhouse Vaults are curated and operated by Carniceria Tropical SA.
    </p>
  </div>
)

const steakhouseEcosystemDisclosure = (
  <div>
    <p>
      Ecosystem Vaults are designed to support partner protocols by accepting their ecosystem assets
      as collateral, creating aligned liquidity while broadening market participation. Because these
      vaults are directly linked to the performance and liquidity of a limited number of issuers,
      depositors may be exposed to higher volatility, concentration risk, or liquidity fluctuations
      compared to vaults backed by more diversified assets.
    </p>
    <p>
      We encourage participants to review our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/risk-management">
          Risk Documentation
        </ExternalLink>
      </span>{' '}
      for a detailed explanation of how we assess market volatility and counterparty risk, and
      maintain robust safeguards. Further details on vault structures can be found on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/products">
          Products page
        </ExternalLink>
      </span>
      . Each depositor acknowledges having read and understood our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.steakhouse.financial/docs/documents/disclaimers/vaults">
          Disclaimers
        </ExternalLink>
      </span>
      . All Steakhouse Vaults are curated and operated by Carniceria Tropical SA.
    </p>
  </div>
)

const apyxDisclosure = (
  <div>
    <p>
      The underlying markets are conservatively parameterized to handle collateral volatility, but
      some risk remains and loss of principal is possible. Collateral value can change with market
      conditions and the state of the Apyx protocol, and in the event of a sharp drop in value, a
      depeg, or a protocol level failure, liquidations may not execute smoothly, potentially leading
      to losses. Other risks, including those related to smart contracts and oracles, also apply, so
      please review carefully before depositing.
    </p>
  </div>
)

const clearstarCoreDisclosure = (
  <div>
    <p>
      Clearstar Core Vaults are positioned between our high-yield and conservative exposure
      categories, aiming to balance return and risk through a diversified mix of collateral. By
      blending established, liquid assets with a measured allocation to higher-yielding positions,
      these vaults aim for delivering steady and competitive performance while maintaining
      sufficient liquidity during normal market conditions. This balance does not remove risk
      however, as shifts in market conditions can still affect valuations and, in stressed
      scenarios, may lead to liquidations or temporary liquidity constraints in the underlying
      markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstarBoringDisclosure = (
  <div>
    <p>
      Clearstar Boring Vaults are built to keep volatility low by allocating systematically toward
      deeply liquid, blue-chip collateral. Although these vaults concentrate on mature,
      well-established markets, no allocation strategy can remove risk altogether, exceptional
      market conditions or tail events may still produce abrupt price swings and heightened
      volatility in the underlying Morpho markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstar3JaneDisclosure = (
  <div>
    <p>
      3Jane Ecosystem Vault allocates exclusively to assets originating from the 3Jane protocol and
      its associated markets. This focused approach gives depositors concentrated exposure to the
      3Jane ecosystem, which carries risks distinct from our diversified vaults. Because allocations
      are confined to a single protocol, the performance of these vaults is closely tied to the
      health, security, and adoption of 3Jane; including its smart contract integrity, governance
      decisions, and token or collateral dynamics. Adverse developments specific to 3Jane, such as
      protocol exploits, governance changes, or a decline in liquidity, could materially affect
      vault performance and may lead to liquidations or withdrawal constraints in the underlying
      Morpho markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstarReDisclosure = (
  <div>
    <p>
      Re Ecosystem Vault allocate exclusively to assets originating from the Re protocol and its
      associated markets. This focused approach gives depositors concentrated exposure to the Re
      ecosystem, which carries risks distinct from our diversified vaults. Because allocations are
      confined to a single protocol, the performance of these vaults is closely tied to the health,
      security, and adoption of Re; including its smart contract integrity, governance decisions,
      and token or collateral dynamics. Adverse developments specific to Re, such as protocol
      exploits, governance changes, or a decline in liquidity, could materially affect vault
      performance and may lead to liquidations or withdrawal constraints in the underlying Morpho
      markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstarCbAssetsDisclosure = (
  <div>
    <p>
      The Clearstar cbAssets Vault allocates exclusively to Morpho markets on Base whose collateral
      is issued or bridged by Coinbase. This gives depositors concentrated exposure to a single
      issuer and custodian, which carries risks distinct from our diversified vaults. Most
      collateral consists of Coinbase wrapped assets, each backed 1:1 by the underlying asset held
      in Coinbase custody and redeemable through a Coinbase account. Their value depends on
      Coinbase’s safekeeping of the reserves, its minting and redemption process, and the integrity
      of the token contracts. cbETH also carries Ethereum staking risks, such as slashing and
      withdrawal delays. SOL and JitoSOL reach Base through the Base-Solana bridge and depend on its
      security; JitoSOL additionally depends on the Jito staking protocol.
    </p>
    <br />
    <p>
      Collateral prices are provided by third-party oracle feeds. Some of these feeds may price a
      wrapped or bridged token at the market price of its underlying asset, in which case they would
      not reflect a loss of backing in the token itself, for example after a custody incident, a
      halt in redemptions or a bridge failure. Clearstar does not control these feeds and accepts no
      responsibility for their accuracy, availability, or any change in their methodology.
    </p>
    <br />
    <p>
      Liquidations depend on on-chain liquidity for each collateral asset on Base, which can be
      limited for the smaller and newer wrapped assets, and on liquidators being able to redeem
      through Coinbase. Adverse developments such as a custody or bridge failure, a suspension of
      minting or redemption, oracle failure or manipulation, regulatory action affecting Coinbase,
      or a sharp decline in liquidity could materially affect vault performance and may lead to bad
      debt, liquidations, or withdrawal constraints in the underlying Morpho markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstarTokenizedStocksDisclosure = (
  <div>
    <p>
      The Clearstar Tokenized Stocks USDC Vault allocates exclusively to Morpho markets
      collateralized by tokenized products issued by Coinbase on Base. This focused approach gives
      depositors concentrated exposure to a single issuer and to a new asset class, which carries
      risks distinct from our diversified vaults. The performance of this vault is closely tied to
      the health, security, and continued operation of Coinbase&apos;s tokenization program,
      including its issuance and redemption arrangements, the legal and regulatory treatment of
      tokenized securities, and the smart contract integrity of the tokens themselves.
    </p>
    <br />
    <p>
      Collateral prices are provided by Chainlink price feeds. The methodology of these feeds is
      expected to evolve over time, for example with the addition of more venues for reporting
      prices during overnight sessions and/or weekends and bank holidays when the stock markets are
      closed. Clearstar does not control these feeds and accepts no responsibility for their
      accuracy, availability, or any change in their methodology.
    </p>
    <br />
    <p>
      Because the underlying equities trade only during exchange hours while the on-chain markets
      operate continuously, collateral values can gap at market open, feed updates may lag or pause
      outside trading hours, and liquidations may face thin on-chain liquidity. Adverse developments
      such as feed failure or manipulation, a suspension of issuance or redemption, regulatory
      action against tokenized securities, or a sharp decline in liquidity could materially affect
      vault performance and may lead to bad debt, liquidations, or withdrawal constraints in the
      underlying Morpho markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. By depositing in this vault, you confirm that you have read and
      accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const clearstarAccountableDisclosure = (
  <div>
    <p>
      Clearstar Accountable AUSD Vault allocates exclusively to assets originating from the
      Accountable protocol and its associated markets. This focused approach gives depositors
      concentrated exposure to the Accountable ecosystem, which carries risks distinct from our
      diversified vaults. Because allocations are confined to a single protocol, the performance of
      these vaults is closely tied to the health, security, and adoption of Accountable; including
      its smart contract integrity, governance decisions, and token or collateral dynamics. Adverse
      developments specific to Accountable, such as protocol exploits, governance changes, or a
      decline in liquidity, could materially affect vault performance and may lead to liquidations
      or withdrawal constraints in the underlying Morpho markets.
    </p>
    <br />
    <p>
      Before depositing, we invite users to consult our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://sticky-burst-444.notion.site/Capital-Allocation-Framework-3208ae5ce0f88034a5cfe519bce01098">
          Risk Framework
        </ExternalLink>
      </span>{' '}
      for an in-depth overview of how we evaluate market and counterparty exposures and the
      safeguards we keep in place. Additional information on the due diligence and identified risks
      of selected collaterals is available on our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/reports/">Reports page</ExternalLink>
      </span>
      . By depositing in this vault, you confirm that you have read and accepted our{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://www.clearstar.xyz/legal/terms-of-service.html">
          Terms of Service
        </ExternalLink>
      </span>
      . All Vaults under the Clearstar Labs brand name are curated and operated by NovaFusion SA.
    </p>
  </div>
)

const bitwisePremiumRwaAusdDisclosure = (
  <div>
    <h2>
      <strong>Risks and Important Information</strong>
    </h2>
    <br />
    <p>
      This vault is a technology-mediated lending protocol in which users supply AUSD to a lending
      pool and earn variable interest paid by borrowers. It is not a managed fund, investment
      product, or common enterprise. Yield is generated from algorithmically determined interest
      based on borrower demand and protocol utilization — not from the entrepreneurial or managerial
      efforts of Bitwise or any other person.
    </p>
    <br />
    <p>
      Vault Curation Services for this vault are provided by Bitwise Investment Manager, LLC
      (“BIM”). BIM is registered with the U.S. Securities and Exchange Commission (“SEC”) as an
      investment adviser.{' '}
      <strong>
        That registration relates to BIM&apos;s separately conducted advisory business. BIM is not
        acting as an investment adviser, fiduciary, or in any advisory capacity in connection with
        this vault. The SEC has not approved or endorsed this vault or any vault product. No
        regulatory protections afforded to BIM&apos;s investment advisory clients extend to vault
        users. No advisory, fiduciary, trust, or client relationship is created between BIM and any
        vault user.
      </strong>
    </p>
    <br />
    <p>
      Custody of assets deposited into the vault is maintained through user-controlled wallets and
      the onchain smart contract infrastructure of the Morpho protocol. Bitwise does not custody
      user assets.
    </p>
    <br />
    <ul>
      <li>
        <strong>Risk factors.</strong> Participation in DeFi lending carries material risks,
        including the risk of total loss of deposited assets. Risk factors include, but are not
        limited to:
      </li>
      <li>
        <strong>Smart contract risk.</strong> This vault relies on smart-contract code that may
        contain bugs, vulnerabilities, or exploitable weaknesses. A smart-contract exploit could
        result in partial or total loss of all deposited assets. Smart-contract audits are available
        at{' '}
        <span className="text-info-foreground text-xs">
          <ExternalLink href="https://docs.morpho.org/get-started/resources/audits/">
            docs.morpho.org/get-started/resources/audits
          </ExternalLink>
        </span>
        .
      </li>
      <li>
        <strong>Collateral and credit risk.</strong> If borrower collateral values decline rapidly,
        liquidation proceeds may be insufficient to cover outstanding loans, resulting in bad debt
        allocated to depositors. The quality, liquidity, and volatility of accepted collateral types
        directly affects the risk profile of this vault.
      </li>
      <li>
        <strong>RWA-specific risk.</strong> Tokenized real-world assets carry credit, structural,
        and provider-specific risks, including valuation risk, custodial risk, legal and enforcement
        risk, and redemption risk. The collateral characteristics, default history, insurance
        arrangements, and structural features described above are as represented by the respective
        third-party providers; Bitwise has not independently verified this information and makes no
        representation or warranty regarding its accuracy or completeness.
      </li>
      <li>
        <strong>Liquidity risk.</strong> Withdrawal is subject to available liquidity in the
        underlying lending pool. If utilization is high, withdrawal may be delayed or temporarily
        unavailable.
      </li>
      <li>
        <strong>Oracle risk.</strong> The lending protocol relies on price oracles. Oracle failure,
        manipulation, or stale data could result in improper liquidations or failure to liquidate
        undercollateralized positions.
      </li>
      <li>
        <strong>Regulatory risk.</strong> The regulatory classification of DeFi lending protocols,
        vault interests, and digital assets is uncertain and evolving. Regulatory action may
        restrict, prohibit, or alter the availability or structure of this vault.
      </li>
      <li>
        <strong>Protocol risk.</strong> The underlying Morpho protocol is operated by third parties.
        Bitwise does not control the operation, security, governance, or solvency of the protocol.
      </li>
      <li>
        <strong>Variable yield.</strong> Yields are variable, not guaranteed, and may be zero.
      </li>
      <li>
        <strong>No insurance.</strong> This vault is <strong>not</strong> insured by the Federal
        Deposit Insurance Corporation (FDIC), the Securities Investor Protection Corporation (SIPC),
        or any other governmental or private insurance program.{' '}
        <strong>You may lose some or all of your deposited assets.</strong>
      </li>
    </ul>
    <br />
    <p>
      Use of this vault is governed by the Bitwise Onchain Terms of Use, including Schedule A:
      Lending Vault Terms, available at{' '}
      <span className="text-info-foreground text-xs">
        <ExternalLink href="https://onchain.bitwiseinvestments.com/tos">
          onchain.bitwiseinvestments.com/tos
        </ExternalLink>
      </span>
      .
    </p>
  </div>
)

const nineSummitsPikuDisclosure = (
  <div>
    <p>
      <strong>You are exposed to the Piku ecosystem, and you bear its risk.</strong>
      <br />
      The vault deploys USDC into &quot;Piku Ecosystem Vaults&quot; markets on Morpho. Your USDC is
      supplied against collateral that is itself a share of a Morini strategy vault. 9Summits
      curates and optimises liquidity on those markets; we do not control or guarantee the
      underlying strategies.
    </p>
    <br />
    <p>
      <strong>The collateral is non-liquid and priced off NAV.</strong>
      <br />
      The collateral is made of strategy vault shares (Morini CarryTradeUSDTRYLeverage,
      StockMarketTRBasisTrade, FXArbUSDTRY, USP). Morpho{"'"}s oracle prices these off the
      underlying strategy{"'"}s net asset value, not a live traded market price. There is no deep
      secondary market to exit these positions quickly. If a strategy{"'"}s NAV falls, the price the
      protocol sees falls with it.
    </p>
    <br />
    <p>
      <strong>Redemption risk.</strong>
      <br />
      Underlying strategy vaults may restrict, delay, or freeze redemptions, in some cases without
      immediately marking down NAV. If redemptions freeze, the vault cannot exit those markets on
      demand, and the liquidity available to withdrawing depositors is limited to what remains
      liquid.
    </p>
    <br />
    <p>
      <strong>No principal protection. No guaranteed yield.</strong>
      <br />
      APY is variable, reflects current market conditions, and can fall. This vault does not
      guarantee your principal or any stated yield against strategy or market loss.
    </p>
  </div>
)

/**
 * Risk disclosure data organized by chain ID and vault/market address
 */
export const riskDisclosures: Record<ChainId, RiskDisclosuresMapping | undefined> = {
  [ChainId.EthMainnet]: {
    // Cap Ecosystem USDC
    '0x0B2d98BBF3E38Df1D1B7Be7343732E32e8B1F818': {
      disclosure: gauntletDisclosure
    },

    // Elixir USDC
    '0x0404fD1a77756EB029F06b5CDea88B2B2ddC2fEE': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet AUSD Core
    '0x1B4cd53a1A8e5F50aB6320EF34E5fB4D3df7B6f6': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet cbBTC Core
    '0xF587f2e8AfF7D76618d3B6B4626621860FbD54e3': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet DAI Core
    '0x500331c9fF24D9d11aee6B07734Aa72343EA74a5': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet EURC Core
    '0x2ed10624315b74a78f11FAbedAa1A228c198aEfB': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet EURC Core V2
    '0x842A84Df61DbE701c5dAE345F3f8E64aF727E48C': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet eUSD Core
    '0xc080f56504e0278828A403269DB945F6c6D6E014': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet LBTC Core
    '0xdC94785959B73F7A168452b3654E44fEc6A750e4': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet LRT Core
    '0x4881Ef0BF6d2365D3dd6499ccd7532bcdBCE0658': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet MKR Blended
    '0xEbFA750279dEfa89b8D99bdd145a016F6292757b': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDA Core
    '0x125D41A6e5dbf455cD9Df8F80BCC6fd172D52Cc6': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Core
    '0x8eB67A509616cd6A7c1B3c8C21D48FF57df3d458': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Frontier
    '0xc582F04d8a82795aa2Ff9c8bb4c1c889fe7b754e': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Frontier V2
    '0x9a1D6bd5b8642C41F25e0958129B85f8E1176F3e': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime
    '0xdd0f28e19C1780eb6396170735D45153D261490d': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime V2
    '0x8c106EEDAd96553e64287A5A6839c3Cc78afA3D0': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC RWA
    '0xA8875aaeBc4f830524e35d57F9772FfAcbdD6C45': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT Frontier
    '0x79FD640000F8563A866322483524a4b48f1Ed702': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT Frontier V2
    '0xE571B648569619566CF6ce1060C97B621CB635D3': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT Prime
    '0x8CB3649114051cA5119141a34C200D65dc0Faa73': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT Prime V2
    '0xf3557AD5E984211ac8A0874A670344f2C3376471': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WBTC Core
    '0x443df5eEE3196e9b2Dd77CaBd3eA76C3dee8f9b2': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Balanced V2
    '0x5f342382D5F77F0f99E8f26161E689df6c7Cded3': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Ecosystem
    '0x1e6ffa4e9F63d10B8820A3ab52566Af881Dab53c': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Prime
    '0x2371e134e3455e0593363cBF89d3b6cf53740618': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Prime V2
    '0x43fCd85E8D9D003D515f886891B7C742AC9f92da': {
      disclosure: gauntletDisclosure
    },

    // Index Coop hyETH
    '0x701907283a57FF77E255C3f1aAD790466B8CE4ef': {
      disclosure: gauntletDisclosure
    },

    // IndexCoop hyETH
    '0xc554929a61d862F2741077F8aafa147479c0b308': {
      disclosure: gauntletDisclosure
    },

    // Metronome msETH Vault
    '0x78B18E07dc43017fcEaabaD0751d6464c0F56b25': {
      disclosure: gauntletDisclosure
    },

    // Metronome msUSD Vault
    '0x6859B34a9379122d25A9FA46f0882d434fee36c3': {
      disclosure: gauntletDisclosure
    },

    // Osero x Gauntlet USDC Prime
    '0x802148D518A6De2aF866f9A61ffB5e5C39156dB2': {
      disclosure: gauntletDisclosure
    },

    // Resolv USDC
    '0x132E6C9C33A62D7727cd359b1f51e5B566E485Eb': {
      disclosure: gauntletDisclosure
    },

    // SolvBTC Babylon Vault
    '0xdBB316375B4dC992B2c8827D120c09dFB1d3455D': {
      disclosure: gauntletDisclosure
    },

    // SwissBorg Morpho USDC
    '0x4Ff4186188f8406917293A9e01A1ca16d3cf9E59': {
      disclosure: gauntletDisclosure
    },

    // SwissBorg tBTC
    '0x059Fc6723b9bF77DbF4283C8d7C90eA8Af44EF10': {
      disclosure: gauntletDisclosure
    },

    // Uniswap ETH V2
    '0x98D2b241DA14c5dd848812708Eb8A1F3c5512f9d': {
      disclosure: gauntletDisclosure
    },

    // Uniswap USDC V2
    '0x5B453493D2328E7F747eb2e66446eFe707728be7': {
      disclosure: gauntletDisclosure
    },

    // Uniswap USDT V2
    '0xb8274eFADB953FE9ae052D481a3FC5B6A3ceD703': {
      disclosure: gauntletDisclosure
    },

    // Vault Bridge USDT
    '0xc54b4E08C1Dcc199fdd35c6b5Ab589ffD3428a8d': {
      disclosure: gauntletDisclosure
    },

    // Vault Bridge WBTC
    '0x812B2C6Ab3f4471c0E43D4BB61098a9211017427': {
      disclosure: gauntletDisclosure
    },

    // Vault Bridge WETH
    '0x31A5684983EeE865d943A696AAC155363bA024f9': {
      disclosure: gauntletDisclosure
    },

    // 9Summits Piku Ecosystem USDC
    '0xC30C60DE46dEc551B96326cBD05592C9245773ef': {
      disclosure: nineSummitsPikuDisclosure
    },

    // Bitwise Premium RWA AUSD
    '0xB344e331A3cDa61D329fb3Cca2Be5942da87c418': {
      disclosure: bitwisePremiumRwaAusdDisclosure
    },

    // Clearstar USDC CORE
    '0x69A238Ae7ebeb3c53ff3B544E48B96a2142fc284': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar ETH Reactor
    '0xae9A5aa54ae43BB8811435F02A29E9d2B43CDC7C': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar Boring USDC
    '0xF3Cc5c9A25508D8d959618fd48F6AbC18cA4Db49': {
      disclosure: clearstarBoringDisclosure
    },

    // Clearstar Boring USDT
    '0x2B58132964f038461E3D8b56df582f49FEcc8745': {
      disclosure: clearstarBoringDisclosure
    },

    // 3Jane Ecosystem Vault
    '0xe05faDf242331808f504661BEA65972594869826': {
      disclosure: clearstar3JaneDisclosure
    },

    // Re Ecosystem Vault
    '0xD1E9242e075Db4bdd3f3c721D7d5fd4180A94A7e': {
      disclosure: clearstarReDisclosure
    },

    // Alpha USDC Core
    '0xb0f05E4De970A1aaf77f8C2F823953a367504BA9': {
      disclosure: (
        <div>
          <p>
            Alpha Morpho Vaults are designed to help users earn optimized, risk-adjusted returns by
            participating in on-chain credit markets. While these systems are thoughtfully built
            with security and transparency in mind, it&apos;s important to understand that certain
            risks do exist when engaging with DeFi lending protocols.
          </p>
          <br />
          <p>
            As with any smart contract, there is a small possibility of bugs or vulnerabilities.
            Vaults also depend on external price feeds (oracles) to function accurately, and
            fluctuations in liquidity or collateral values can impact how vaults perform. That said,
            these systems typically include safeguards such as overcollateralization, liquidation
            buffers, and curator oversight to help maintain overall stability.
          </p>
          <br />
          <p>
            Users should also be aware that yield in these vaults depends on lending activity and
            market conditions. In rare cases, high utilization or sudden withdrawals may temporarily
            affect access to liquidity. Governance processes, including time-locked updates, are in
            place to help manage these situations transparently.
          </p>
          <br />
          <p>
            Overall, Alpha Morpho Vaults aim to strike a strong balance between performance and
            safety. We encourage users to review vault strategies, remain informed, and invest
            according to their individual risk tolerance.
          </p>
        </div>
      )
    },

    // Alpha wETH Core
    '0x47fe8Ab9eE47DD65c24df52324181790b9F47EfC': {
      disclosure: (
        <div>
          <p>
            Alpha Morpho Vaults are designed to help users earn optimized, risk-adjusted returns by
            participating in on-chain credit markets. While these systems are thoughtfully built
            with security and transparency in mind, it&apos;s important to understand that certain
            risks do exist when engaging with DeFi lending protocols.
          </p>
          <br />
          <p>
            As with any smart contract, there is a small possibility of bugs or vulnerabilities.
            Vaults also depend on external price feeds (oracles) to function accurately, and
            fluctuations in liquidity or collateral values can impact how vaults perform. That said,
            these systems typically include safeguards such as overcollateralization, liquidation
            buffers, and curator oversight to help maintain overall stability.
          </p>
          <br />
          <p>
            Users should also be aware that yield in these vaults depends on lending activity and
            market conditions. In rare cases, high utilization or sudden withdrawals may temporarily
            affect access to liquidity. Governance processes, including time-locked updates, are in
            place to help manage these situations transparently.
          </p>
          <br />
          <p>
            Overall, Alpha Morpho Vaults aim to strike a strong balance between performance and
            safety. We encourage users to review vault strategies, remain informed, and invest
            according to their individual risk tolerance.
          </p>
        </div>
      )
    },

    // Apyx USDC
    '0x069662D2588fcaC24B5c209456Db965D151556f0': {
      disclosure: apyxDisclosure
    },

    // Apyx apxUSD
    '0x3a618E9D4159dff94E90bd6239161dE4dd7C0082': {
      disclosure: apyxDisclosure
    },

    // Relend USDC
    '0x0F359FD18BDa75e9c49bC027E7da59a4b01BF32a': {
      disclosure: (
        <div>
          <p>
            Relend Network Vaults are curated by B.Protocol and the RiskDAO who curated over $200M
            TVL in Morpho, and consulted in the past to over 10 different lending markets.
          </p>
          <br />
          <p>
            Our vaults aim to achieve superior risk-adjusted returns by capitalizing on distinctive
            market opportunities — which may subject depositors to increased volatility or periods
            of limited liquidity. While continuous oversight and adaptive asset allocation help
            reduce the risk of significant losses, sudden changes in market sentiment can still lead
            to liquidations or temporary liquidity challenges.
          </p>
          <br />
          <p>
            The vault also plays a key role in the Relend Network protocol, and has the ability to
            relend the supplied assets into other chains.
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://docs.relend.network/relend.docs/rusdc-mechanism-and-risk-disclosure">
                See more details here
              </ExternalLink>
            </span>
            .
          </p>
        </div>
      )
    },

    // Smokehouse USDC
    '0xBEeFFF209270748ddd194831b3fa287a5386f5bC': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield Instant USDC V2
    '0xbeeff2C5bF38f90e3482a8b19F12E5a6D2FCa757': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse RUSD
    '0xBeEf11eCb698f4B5378685C05A210bdF71093521': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse USDT
    '0xbEef047a543E45807105E51A8BBEFCc5950fcfBa': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant USDT V2
    '0xbeef003C68896c7D2c3c60d363e8d71a49Ab2bf9': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse ETH
    '0xBEEf050ecd6a16c4e7bfFbB52Ebba7846C4b8cD4': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant ETH V2
    '0xbeef0046fcab1dE47E41fB75BB3dC4Dfc94108E3': {
      disclosure: steakhousePrimeDisclosure
    },

    // Coinshift USDL
    '0xbEeFc011e94f43b8B7b455eBaB290C7Ab4E216f1': {
      disclosure: (
        <div>
          <p>
            Custom Vaults are co-branded with external partners, allowing Steakhouse Financial to
            apply its risk-management expertise in a specialized setting. Depending on each
            partner&apos;s strategic direction, the vault&apos;s underlying assets may range from
            conservative to opportunistic, potentially leading to different liquidity profiles or
            price sensitivities.
          </p>
          <br />
          <p>
            Since each Custom Vault is managed in close collaboration with our partners, management
            parameters may differ slightly from standard Steakhouse or Smokehouse offerings.
            Nonetheless, all critical updates—including fee changes, asset rebalancing, or
            membership criteria—remain subject to formal proposals and time-delayed execution
            through the following Aragon DAO Guardian link:{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://csusdl-2-guardian.dao.eth/">
                csusdl-2-guardian.dao.eth
              </ExternalLink>
            </span>
          </p>
          <br />
          <p>
            For an overview of our broader methodology, see the Steakhouse{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://docs.google.com/document/d/18ADHX_O2oQKjAfRLoLVbDUizKxWBA4w7g8KYsphj0N4/edit?usp=sharing">
                &quot;Risk Framework for Crypto Assets&quot;
              </ExternalLink>
            </span>
            . Further explanations on co-branded vault structures can be found at{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://forum.morpho.org/t/key-information-on-steakhouse-branded-vaults/1343">
                Key Information on Steakhouse Branded Vaults
              </ExternalLink>
            </span>
            .
          </p>
        </div>
      )
    },

    // Steakhouse PYUSD
    '0xbEEF02e5E13584ab96848af90261f0C8Ee04722a': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant PYUSD V2
    '0xbeef00B5d83C1188F07A5184230a805639c39f04': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse USDC
    '0xBEEF01735c132Ada46AA9aA4c54623cAA92A64CB': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant USDC V2
    '0xbeef088055857739C12CD3765F20b7679Def0f51': {
      disclosure: steakhousePrimeDisclosure
    },

    // 3F x Steakhouse USDC V2
    '0xBEEf3f3A04e28895f3D5163d910474901981183D': {
      disclosure: steakhousePrimeDisclosure
    },

    // Smokehouse DAI
    '0xbeeFfF68CC520D68f82641EFF84330C631E2490E': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse Reservoir USDC
    '0xbeEF346d7099865208Ff331e4f648f4154DDAa05': {
      disclosure: steakhouseEcosystemDisclosure
    },

    // Steakhouse Reservoir USDC V2
    '0xBEeFF047C03714965a54b671A37C18beF6b96210': {
      disclosure: steakhouseEcosystemDisclosure
    },

    // Steakhouse USDCV
    '0xbEef11A6Ca3C67bbEa90e5601beDBE78832dd789': {
      disclosure: steakhousePrimeDisclosure
    },

    // Smokehouse ETH
    '0xbEEF36A5C1372F8D7d211527FCE9f83FE02d8A73': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse Ethena USDtb
    '0xBeEFC1CDAfc5b4a649b54D07AFc6bF0f75C6F4E2': {
      disclosure: steakhouseEcosystemDisclosure
    },

    // Vault Bridge USDC
    '0xBEefb9f61CC44895d8AEc381373555a64191A9c4': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse infiniFi USDC
    '0xBEeF1f5Bd88285E5B239B6AAcb991d38ccA23Ac9': {
      disclosure: steakhouseEcosystemDisclosure
    },

    // Steakhouse USDQ
    '0xA1b60d96e5C50dA627095B9381dc5a46AF1a9a42': {
      disclosure: steakhousePrimeDisclosure
    },

    // Smokehouse USDT
    '0xA0804346780b4c2e3bE118ac957D1DB82F9d7484': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield Instant USDT V2
    '0xbeeff07d991C04CD640DE9F15C08ba59c4FEDEb7': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Smokehouse WSTETH
    '0x833AdaeF212c5cD3f78906B44bBfb18258F238F0': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse EURCV
    '0x75741A12B36D181f44F389E0c6B1E0210311e3Ff': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant EURCV V2
    '0xbeef0C075Da5D01112AE5cF34d257074fB5DDB2f': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse USDC RWA
    '0x6D4e530B8431a52FFDA4516BA4Aadc0951897F8C': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse USDR
    '0x30881Baa943777f92DC934d53D3bFdF33382cab3': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse USDT Lite
    '0x097FFEDb80d4b2Ca6105a07a4D90eB739C45A666': {
      disclosure: steakhousePrimeDisclosure
    },

    // Clearstar USDC Reactor
    '0x62fE596d59fB077c2Df736dF212E0AFfb522dC78': {
      disclosure: clearstarDisclosure
    },

    // BOLD Reactor
    '0xfBC84DBfAA2196853FdB6cAE52ef8F56DD76770a': {
      disclosure: clearstarDisclosure
    },

    // Clearstar Institutional USDT
    '0xbF34DE704C23E3Ad125c3f5e1510dadcd27B298D': {
      disclosure: clearstarDisclosure
    },

    // kpk USDC Prime
    '0xe108fbc04852B5df72f9E44d7C29F47e7A993aDd': {
      disclosure: kpkDisclosure
    },

    // kpk ETH Prime
    '0xd564F765F9aD3E7d2d6cA782100795a885e8e7C8': {
      disclosure: kpkDisclosure
    },

    // kpk EURC Yield
    '0x0c6aec603d48eBf1cECc7b247a2c3DA08b398DC1': {
      disclosure: kpkDisclosure
    },

    // kpk USDC Yield (V1)
    '0x9178eBE0691593184c1D785a864B62a326cc3509': {
      disclosure: kpkDisclosure
    },

    // kpk ETH Yield (V2)
    '0x5dbf760b4fd0cDdDe0366b33aEb338b2A6d77725': {
      disclosure: kpkDisclosure
    },

    // Galaxy USDC Quality
    '0x91600E31fBeDc72433d4a57F16639cfe661Be7d8': {
      disclosure: galaxyDisclosure
    },

    // Galaxy USDT Quality
    '0x71ffB6a81786eC285D429d531Cf655107B9D878d': {
      disclosure: galaxyDisclosure
    },

    // Galaxy WETH Quality
    '0x1878805799273d10aE96a58201A6f5254CF9824F': {
      disclosure: galaxyDisclosure
    },

    // Galaxy USDC Enhanced
    '0xd95fE7adF5075fad9D6Bf853E0f9Fe53369E8D96': {
      disclosure: galaxyDisclosure
    },

    // Hyperithm USDC Apex
    '0x777791C4d6DC2CE140D00D2828a7C93503c67777': {
      disclosure: (
        <div>
          <p>
            The Hyperithm USDC Apex Vault aims to capture high yield opportunities by rapidly
            integrating new collateral assets and actively optimizing positions. At the same time,
            we seek to maintain stability through internally developed risk management processes.
          </p>
          <br />
          <p>
            That said, some collateral assets may be highly volatile or have limited market depth.
            Depositors should be aware that this can increase exposure to market risk, liquidity
            risk, and oracle risk.
          </p>
          <br />
          <p>
            The Hyperithm Vault is operated under a robust risk analysis framework that continuously
            reviews factors such as collateral selection, liquidity conditions, liquidation
            slippage, oracle robustness, and asset concentration. While these procedures are
            designed to enhance the resilience of the Vault, sharp market movements or liquidity
            stress in the underlying markets may still result in rapid price changes, liquidation
            events, or temporary withdrawal delays.
          </p>
          <br />
          <p>
            We strongly encourage depositors to fully understand the volatility and liquidity
            characteristics of the assets that may be included in this Vault before participating.
          </p>
        </div>
      )
    },

    // Hyperithm Saturn USDC
    '0xAbe418cc8c06D265E4EB009C02eA4B265eCA7240': {
      disclosure: hyperithmSaturnDisclosure
    },

    // Flowdesk AUSD RWA Strategy
    '0x32401B9fb79065Bc15949DE0BD43927492f02F0C': {
      disclosure: (
        <div>
          <p>
            Flowdesk AUSD RWA Strategy generates yield by allocating AUSD liquidity into a set of
            Morpho markets backed by tokenized real-world asset collateral. The strategy deploys
            both newly created markets and established institutional-grade markets, all subject to
            continuous monitoring by Flowdesk&apos;s risk team.
          </p>
          <br />
          <p>
            No strategy eliminates risk entirely. Depositors should be aware that RWA collateral
            illiquidity, redemption delays, oracle failures, smart contract vulnerabilities, or
            extraordinary market events may disrupt the underlying Morpho markets and affect the
            vault&apos;s ability to generate returns or process withdrawals in a timely manner.
          </p>
          <br />
          <p>
            Flowdesk encourages participants to review the documentation available on the xStocks
            website for a detailed explanation of the underlying collateral, its backing, and
            associated mechanisms. Depositors and borrowers should also review Agora&apos;s
            documentation to understand the mechanics behind AUSD. Each depositor acknowledges
            having read and understood Flowdesk&apos;s Disclaimers.
          </p>
          <br />
          <p>
            All reference to Flowdesk is used for convenience to refer to Flowdesk entities as a
            group. The transaction contemplated herein will not be entered into with Flowdesk SAS
            directly, but the vault is curated and operated by Limitless Frontier Corp.
          </p>
        </div>
      )
    },

    // f(x) Protocol Re7 USDC
    '0x4F460bb11cf958606C69A963B4A17f9DaEEea8b6': {
      disclosure: re7Disclosure
    },

    // Re7 cbBTC
    '0xA02F5E93f783baF150Aa1F8b341Ae90fe0a772f7': {
      disclosure: re7Disclosure
    },

    // Re7 Falcon USDf
    '0x4d52545235A3dF246a8928c583E47AE7eeC4aCfE': {
      disclosure: re7Disclosure
    },

    // Re7 FRAX
    '0xBE40491F3261Fd42724F1AEb465796eb11c06ddF': {
      disclosure: re7Disclosure
    },

    // Re7 tBTC
    '0x43fD147d5319B8Cf39a6e57143684Efca9CF3613': {
      disclosure: re7Disclosure
    },

    // Re7 USDA
    '0x89D80f5e9BC88d8021b352064ae73F0eAf79EBd8': {
      disclosure: re7Disclosure
    },

    // Re7 USDC
    '0x60d715515d4411f7F43e4206dc5d4a3677f0eC78': {
      disclosure: re7Disclosure
    },

    // Re7 USDC Core
    '0x341193ED21711472e71aECa4A942123452bd0ddA': {
      disclosure: re7Disclosure
    },

    // Re7 USDC Prime
    '0x64964E162Aa18d32f91eA5B24a09529f811AEB8e': {
      disclosure: re7Disclosure
    },

    // Re7 USDC Prime V2
    '0x04447273Ee808702a4855403af0acE8E7c042122': {
      disclosure: re7Disclosure
    },

    // Re7 USDT
    '0x95EeF579155cd2C5510F312c8fA39208c3Be01a8': {
      disclosure: re7Disclosure
    },

    // Re7 WBTC
    '0xE0C98605f279e4D7946d25B75869c69802823763': {
      disclosure: re7Disclosure
    },

    // Re7 WETH
    '0x78Fc2c2eD1A4cDb5402365934aE5648aDAd094d0': {
      disclosure: re7Disclosure
    },

    // Re7 WETH V2
    '0x5181cD56C2C71d20094d23d43F4D6362834643AE': {
      disclosure: re7Disclosure
    },

    // Re7 wstETH
    '0xE87ed29896B91421ff43f69257ABF78300e40c7a': {
      disclosure: re7Disclosure
    },

    // AlphaPing Chaince USDC High Yield
    '0xd0634BF57921Ef4068f284F5e78A3f2623e20129': {
      disclosure: (
        <div>
          <p>
            By depositing into this vault, users acknowledge that on-chain lending and digital-asset
            markets involve risk, including potential loss of capital, smart contract risk,
            collateral volatility, liquidity constraints, and protocol-level failure events. Users
            should review the{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://alphaping.ch/risk-disclosure">
                AlphaPing Risk Disclosure
              </ExternalLink>
            </span>{' '}
            and{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://alphaping.ch/terms">AlphaPing Terms</ExternalLink>
            </span>{' '}
            before participating.
          </p>
        </div>
      )
    }
  },

  [ChainId.BaseMainnet]: {
    // Bitso USDC Prime V2
    '0x39E18c066187c83e860801Befe45aE8ecd021575': {
      disclosure: gauntletDisclosure
    },

    // Extrafi XLend USDC
    '0x23479229e52Ab6aaD312D0B03DF9F33B46753B5e': {
      disclosure: gauntletDisclosure
    },

    // Extrafi XLend WETH
    '0x5A32099837D89E3a794a44fb131CBbAD41f87a8C': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet cbBTC Core
    '0x6770216aC60F634483Ec073cBABC4011c94307Cb': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet EURC Balanced V2
    '0x94Af495DE1F56Aa5576dEB17986bDCeE5Dd9778D': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet EURC Core
    '0x1c155be6bC51F2c37d472d4C2Eba7a637806e122': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet LBTC Core
    '0x0D05e6ec0A10f9fFE9229EAA785c11606a1d13Fb': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet sUSDS Core
    '0x0FE5b4aF0337Fd5b2E1675D5f5E8c9101E4D3c7e': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Core
    '0xc0c5689e6f4D256E861F65465b691aeEcC0dEb12': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Frontier
    '0x236919F11ff9eA9550A4287696C2FC9e18E6e890': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Frontier V2
    '0x1deEfABEe758AAbdC29a542B24ca3b75aFD56765': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime
    '0xeE8F4eC5672F09119b96Ab6fB59C27E1b7e44b61': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime V2
    '0x050cE30b927Da55177A4914EC73480238BAD56f0': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Balanced V2
    '0xFeFeC33668E22677c4762d0853d56245a800ff08': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Core
    '0x6b13c060F13Af1fdB319F52315BbbF3fb1D88844': {
      disclosure: gauntletDisclosure
    },

    // Ionic Ecosystem USDC
    '0xCd347c1e7d600a9A3e403497562eDd0A7Bc3Ef21': {
      disclosure: gauntletDisclosure
    },

    // Ionic Ecosystem WETH
    '0x9aB2d181E4b87ba57D5eD564D3eF652C4E710707': {
      disclosure: gauntletDisclosure
    },

    // Juno MXNB Prime V2
    '0x08cc279532f159BdB450eBc0bAf6562f585Ded58': {
      disclosure: gauntletDisclosure
    },

    // Metronome msETH Vault
    '0x43Cd00De63485618A5CEEBE0de364cD6cBeB26E7': {
      disclosure: gauntletDisclosure
    },

    // Clearstar Core ETH
    '0xBCA4E2E24A7cFa776E4282CC8Eb06f04738b71da': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar Core USDC
    '0x116e1A65717A534B73EcB7d4F6543c65DBCd0E46': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar Reactor EURC
    '0x4C7b69b4a82e9E5D8ec60E96516f7A0E17CBC55C': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar Boring USDC
    '0x0282159ecCaabA941bD1f4C518944D8fDCdc0681': {
      disclosure: clearstarBoringDisclosure
    },

    // Clearstar cbAssets Vault
    '0x91C056B6d4311a743614FBc03ac32d4E6A2d3a3c': {
      disclosure: clearstarCbAssetsDisclosure
    },

    // Clearstar Tokenized Stocks USDC
    '0x7aE6f26d0Fd2b510F4ff5A9B167f2d275a7c9dE7': {
      disclosure: clearstarTokenizedStocksDisclosure
    },

    // Seamless cbBTC Vault
    '0x5a47C803488FE2BB0A0EAaf346b420e4dF22F3C7': {
      disclosure: (
        <div>
          <p>
            Gauntlet is the leading model provider in crypto, building optimization strategies for
            tokens, protocols, and chains. Central to our Morpho Vault curation approach is ensuring
            vaults meet our robust risk guidelines, are attractive to the DeFi community, and remain
            resilient enough to handle inherent crypto market volatility.
          </p>
          <br />
          <p>
            This Ecosystem Vault applies our Core strategy, which allocates to a blend of large and
            lower-cap collateralized markets while ensuring exposure to any one asset remains within
            acceptable bounds. Collateral Selection is based on various factors, including the
            availability of withdrawal liquidity and sufficient liquidity and slippage to perform
            healthy liquidations in severe market conditions.
          </p>
          <br />
          <p>
            For more info, visit{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://gauntlet.gitbook.io/gauntlet-vaultbook/morpho-vaults">
                this link
              </ExternalLink>
            </span>
            .
          </p>
          <br />
          <p>
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://storage.googleapis.com/gauntlet-public-web-assets/Gauntlet_New_Partnership_Disclaimer.pdf">
                Gauntlet Disclaimer
              </ExternalLink>
            </span>
          </p>
        </div>
      )
    },

    // Seamless USDC
    '0x616a4E1db48e22028f6bbf20444Cd3b8e3273738': {
      disclosure: (
        <div>
          <p>
            Gauntlet is the leading model provider in crypto, building optimization strategies for
            tokens, protocols, and chains. Central to our Morpho Vault curation approach is ensuring
            vaults meet our robust risk guidelines, are attractive to the DeFi community, and remain
            resilient enough to handle inherent crypto market volatility.
          </p>
          <br />
          <p>
            This Ecosystem Vault applies our Core strategy, which allocates to a blend of large and
            lower-cap collateralized markets while ensuring exposure to any one asset remains within
            acceptable bounds. Collateral Selection is based on various factors, including the
            availability of withdrawal liquidity and sufficient liquidity and slippage to perform
            healthy liquidations in severe market conditions.
          </p>
          <br />
          <p>
            For more info, visit{' '}
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://gauntlet.gitbook.io/gauntlet-vaultbook/morpho-vaults">
                this link
              </ExternalLink>
            </span>
            .
          </p>
          <br />
          <p>
            <span className="text-info-foreground text-xs">
              <ExternalLink href="https://storage.googleapis.com/gauntlet-public-web-assets/Gauntlet_New_Partnership_Disclaimer.pdf">
                Gauntlet Disclaimer
              </ExternalLink>
            </span>
          </p>
        </div>
      )
    },

    // Steakhouse USDC
    '0xbeeF010f9cb27031ad51e3333f9aF9C6B1228183': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse High Yield USDC
    '0xCBeeF01994E24a60f7DCB8De98e75AD8BD4Ad60d': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse USDA
    '0xbEEfa1aBfEbE621DF50ceaEF9f54FdB73648c92C': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse ETH
    '0xbEEf050a7485865A7a8d8Ca0CC5f7536b7a3443e': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant ETH V2
    '0xbeef00f0A818894a2Cf111644A5098421611100E': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime XSGD
    '0xBeeF0c8333b3De8F0450565834db4EB05dEf7A5F': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime XSGD V2
    '0xbeef00A299ae1784Fb7740d2d469F60a1E015fe9': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse EURC
    '0xBeEF086b8807Dc5E5A1740C5E3a7C4c366eA6ab5': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime EURC V2
    '0xbeef009F28cCf367444a9F79096862920e025DC1': {
      disclosure: steakhousePrimeDisclosure
    },

    // Riva x Steakhouse EURC V2
    '0xbeEf00890534C736186f3126187Da80c961EdCa1': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse EURA
    '0xBEeFA28D5e56d41D35df760AB53B94D9FfD7051F': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime USDC
    '0xBEEFE94c8aD530842bfE7d8B397938fFc1cb83b2': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime Instant USDC V2
    '0xbeef0e0834849aCC03f0089F01f4F1Eeb06873C9': {
      disclosure: steakhousePrimeDisclosure
    },

    // Riva x Steakhouse USDC V2
    '0xbEeF006fb43820C864894892db0eCFEee3FdF587': {
      disclosure: steakhousePrimeDisclosure
    },

    // Farcaster x Steakhouse Prime USDC V2
    '0xBeEF00fc6e87dE086A0e29169A2f6e25cF5C11a9': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse High Yield USDC v1.1
    '0xBEEFA7B88064FeEF0cEe02AAeBBd95D30df3878F': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield Instant USDC V2
    '0xbeeff7aE5E00Aae3Db302e4B0d8C883810a58100': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // High Yield Clearstar USDC
    '0xE74c499fA461AF1844fCa84204490877787cED56': {
      disclosure: clearstarDisclosure
    },

    // Clearstar USDC Reactor
    '0x1D3b1Cd0a0f242d598834b3F2d126dC6bd774657': {
      disclosure: clearstarDisclosure
    },

    // Clearstar ETH Reactor
    '0x09832347586E238841F49149C84d121Bc2191C53': {
      disclosure: clearstarDisclosure
    },

    // Clearstar Boring USDC
    '0x43e623Ff7D14d5b105F7bE9c488F36dbF11D1F46': {
      disclosure: clearstarDisclosure
    },

    // Degen USDC
    '0xdB90A4e973B7663ce0Ccc32B6FbD37ffb19BfA83': {
      disclosure: re7Disclosure
    },

    // Pyth ETH
    '0x80D9964fEb4A507dD697b4437Fc5b25b618CE446': {
      disclosure: re7Disclosure
    },

    // Pyth USDC
    '0x0FaBfEAcedf47e890c50C8120177fff69C6a1d9B': {
      disclosure: re7Disclosure
    },

    // Re7 - Degen
    '0x8c3A6B12332a6354805Eb4b72ef619aEdd22BcdD': {
      disclosure: re7Disclosure
    },

    // Re7 - eUSD
    '0xbb819D845b573B5D7C538F5b85057160cfb5f313': {
      disclosure: re7Disclosure
    },

    // Re7 eUSD V2
    '0xc9C474C6Aa0E930ED42fD1f40be5b1d6A5eAD645': {
      disclosure: re7Disclosure
    },

    // Re7 - MAI
    '0x30B8A2c8E7Fa41e77b54b8FaF45c610e7aD909E3': {
      disclosure: re7Disclosure
    },

    // Re7 cbBTC
    '0xe51F91e68D01530fbbCd3D2c9724E841C06ec827': {
      disclosure: re7Disclosure
    },

    // Re7 cdxUSD
    '0x004Ff85F887aA612CfdAf4B7A19B10eAcfAb32f2': {
      disclosure: re7Disclosure
    },

    // Re7 cdxUSD 1.1
    '0x74B6EA9BFee07C3756969b0139CFacBBa5845969': {
      disclosure: re7Disclosure
    },

    // Re7 eUSD
    '0xBD596239F71ce6BA9993100fbD5Aff366C9366a1': {
      disclosure: re7Disclosure
    },

    // Re7 Resolv USR
    '0x00dfDb8C7295a03DCf1ADfF4D21eB5D9D19FB330': {
      disclosure: re7Disclosure
    },

    // Re7 RWA
    '0x6e37C95b43566E538D8C278eb69B00FC717a001b': {
      disclosure: re7Disclosure
    },

    // Re7 USDA
    '0xDd195bc052f172a996d721B25d587A1ea5B8385D': {
      disclosure: re7Disclosure
    },

    // Re7 USDC
    '0x12AFDeFb2237a5963e7BAb3e2D46ad0eee70406e': {
      disclosure: re7Disclosure
    },

    // Re7 USDC V2
    '0x618495ccC4e751178C4914b1E939C0fe0FB07b9b': {
      disclosure: re7Disclosure
    },

    // Re7 WETH
    '0xA2Cac0023a4797b4729Db94783405189a4203AFc': {
      disclosure: re7Disclosure
    },

    // Re7 WETH V2
    '0xCa120dCc42F45427eD0d0f060d56104c4a8EDEC4': {
      disclosure: re7Disclosure
    },

    // Re7 wstETH
    '0x43338Cc9C514952eBC9c36A842AcEC986EC7CeDB': {
      disclosure: re7Disclosure
    },

    // Galaxy USDC Quality
    '0x1e9e47583f15D45a10Df48c0b1846E0492c795D7': {
      disclosure: galaxyDisclosure
    },

    // Universal USDC
    '0xB7890CEE6CF4792cdCC13489D36D9d42726ab863': {
      disclosure: re7Disclosure
    },

    // Universal USDC V2
    '0x0B7ee82ad75B2D3fc7f3A110A51ba68714171D25': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.PolygonMainnet]: {
    // Compound POL
    '0x3F33F9f7e2D7cfBCBDf8ea8b870a6E3d449664c2': {
      disclosure: gauntletDisclosure
    },

    // Compound USDC
    '0x781FB7F6d845E3bE129289833b04d43Aa8558c42': {
      disclosure: gauntletDisclosure
    },

    // Compound USDT
    '0xfD06859A671C21497a2EB8C5E3fEA48De924D6c8': {
      disclosure: gauntletDisclosure
    },

    // Compound WETH
    '0xF5C81d25ee174d83f1FD202cA94AE6070d073cCF': {
      disclosure: gauntletDisclosure
    },

    // Steakhouse Prime ETH
    '0xa4EDd2D004CBd422DB53ceD0f511072A50a6fD70': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse High Yield USDT0
    '0xB7c9988D3922F25a336a469F3bB26CA61FE79e24': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDC
    '0xAcB0DCe4b0FF400AD8F6917f3ca13E434C9ed6bC': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse Prime USDC
    '0x390ceC023dCd8ebfE3e8904233b20CEbEEC26739': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime USDT0
    '0x057b06D97Ee9c7BE7e26429bB4C9c4cfAEcB23C1': {
      disclosure: steakhousePrimeDisclosure
    },

    // Re7 wPOL
    '0xF91D80E43272DBC610551E8c872E0438d62C1c69': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.ArbitrumMainnet]: {
    // Gauntlet USDC Balanced V2
    '0x55a2B207b0074E13AdCb858950a81B7a04775E0F': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Core
    '0x7e97fa6893871A2751B5fE961978DCCb2c201E65': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime
    '0x7c574174DA4b2be3f705c6244B4BfA0815a8B3Ed': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime V2
    '0x610D151aE40662AE148cdBaaE1Ea5904b6AFAE78': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT0 Core
    '0x139250CdB310D657eAC506c7C7FC6AcDE34Af1ec': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH Core
    '0xBD14bEa2eCECeCD5F32149B0f84BE7f7F446B964': {
      disclosure: gauntletDisclosure
    },

    // Clearstar USDC Reactor
    '0xa53Cf822FE93002aEaE16d395CD823Ece161a6AC': {
      disclosure: clearstarDisclosure
    },

    // Clearstar High Yield USDC
    '0x64CA76e2525fc6Ab2179300c15e343d73e42f958': {
      disclosure: clearstarDisclosure
    },

    // kpk USDC Yield
    '0x2C609d9CfC9dda2dB5C128B2a665D921ec53579d': {
      disclosure: kpkDisclosure
    },

    // Steakhouse High Yield USDC
    '0x5c0C306Aaa9F877de636f4d5822cA9F2E81563BA': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDC V2
    '0xbeeff1D5dE8F79ff37a151681100B039661da518': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse Prime USDC
    '0x250CF7c82bAc7cB6cf899b6052979d4B5BA1f9ca': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime ETH
    '0x0623a67D69bB2F59D266897A15dC1509d291D631': {
      disclosure: steakhousePrimeDisclosure
    },

    // Re7 USDC
    '0x704761B6280BfABC7E397EfD34Fc15cd3b527d7D': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.OptimismMainnet]: {
    // Gauntlet USDC Balanced V2
    '0x4fFc4e5F1f1F5C43dc9Bc27b53728DA13b02bE35': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC Prime
    '0xC30ce6A5758786e0F640cC5f881Dd96e9a1C5C59': {
      disclosure: gauntletDisclosure
    },

    // Re7 WETH V2
    '0x3d63934715b6D4c4DFbBC1a00Fe2A2145079DD76': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.WorldChainMainnet]: {
    // Re7 EURC
    '0xDaa79e066DeE8c8C15FFb37b1157F7Eb8e0d1b37': {
      disclosure: re7Disclosure
    },

    // Re7 USDC
    '0xb1E80387EbE53Ff75a89736097D34dC8D9E9045B': {
      disclosure: re7Disclosure
    },

    // Re7 WARS
    '0x1C94c7A2c71ECF13104c31F49d5138EDb099D25D': {
      disclosure: re7Disclosure
    },

    // Re7 WARS V2
    '0x4047dB25Fd6EcD07d72CA44adf3a2A44dE6DE084': {
      disclosure: re7Disclosure
    },

    // Re7 WBTC
    '0xBC8C37467c5Df9D50B42294B8628c25888BECF61': {
      disclosure: re7Disclosure
    },

    // Re7 WETH
    '0x0Db7E405278c2674F462aC9D9eb8b8346D1c1571': {
      disclosure: re7Disclosure
    },

    // Re7 WLD
    '0x348831b46876d3dF2Db98BdEc5E3B4083329Ab9f': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.FraxtalMainnet]: undefined,
  [ChainId.ScrollMainnet]: undefined,
  [ChainId.InkMainnet]: undefined,
  [ChainId.Unichain]: {
    // Gauntlet USDC
    '0x38f4f3B6533de0023b9DCd04b02F93d36ad1F9f9': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT0
    '0x89849B6e57e1c61e447257242bDa97c70FA99b6b': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH
    '0x830898200F0E8Be8Dc1C9A836f4AB29ECEdf76eb': {
      disclosure: gauntletDisclosure
    },

    // Clearstar USDT0 Reactor
    '0xcAfE4D206acE4aAdE81de1F038AEEe602C5FC64A': {
      disclosure: clearstarDisclosure
    },

    // Clearstar USDC Reactor
    '0x0ebB4cBCd9B92a18a7E282669AC9a7eeEFC829aE': {
      disclosure: clearstarDisclosure
    },

    // Steakhouse ETH
    '0x0993Ae24a988c6925d80aD61344a76B81aA0D34c': {
      disclosure: steakhousePrimeDisclosure
    },

    // Re7 USDC
    '0xa48f8A10F16bB50A588606553D9fE7338050f8Cd': {
      disclosure: re7Disclosure
    },

    // Re7 USDT0
    '0x2c0F7e917257926BA6233B20DE19d7fe3210858C': {
      disclosure: re7Disclosure
    },

    // Re7 WETH
    '0x6af5E46456A8ac80BB53a02799965DEF99c26e52': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.SonicMainnet]: undefined,
  [ChainId.HemiMainnet]: undefined,
  [ChainId.ModeMainnet]: undefined,
  [ChainId.CornMainnet]: undefined,
  [ChainId.PlumeMainnet]: undefined,
  [ChainId.CampMainnet]: undefined,
  [ChainId.KatanaMainnet]: {
    // Gauntlet AUSD
    '0x9540441C503D763094921dbE4f13268E6d1d3B56': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet BTCK
    '0x5BD1D75d45EdB622d0ea50946F2f741CE8dC5b75': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC
    '0xE4248e2105508FcBad3fe95691551d1AF14015f7': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT
    '0x1ecDC3F2B5E90bfB55fF45a7476FF98A8957388E': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT V2
    '0xaC596AD9771a8d0D4DF108ae0406e6f913aEdceb': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WBTC
    '0xf243523996ADbb273F0B237B53f30017C4364bBC': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WETH
    '0xC5e7AB07030305fc925175b25B93b285d40dCdFf': {
      disclosure: gauntletDisclosure
    },

    // Clearstar Core USDT
    '0xb3C62339B31F1f5a2aEaCd743DeA1eC6e2ACADF6': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar Core USDC
    '0x6a31358E58B8692cE5317181ff3379A5766A7ABA': {
      disclosure: clearstarCoreDisclosure
    },

    // Clearstar USDC Reactor
    '0x9aF031182fCe8BF0b296145f2e1f1Df5C3feCDE7': {
      disclosure: clearstarDisclosure
    },

    // Clearstar USDT Reactor
    '0x4105eFe00C840E7d635B723B154aBaE7490E0042': {
      disclosure: clearstarDisclosure
    },

    // Steakhouse High Yield AUSD
    '0xdE6a4F28Acfe431DD1CfA2D9c7A3d8301624a841': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse Prime AUSD
    '0x82c4C641CCc38719ae1f0FBd16A64808d838fDfD': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse Prime USDC
    '0x61D4F9D3797BA4dA152238c53a6f93Fb665C3c1d': {
      disclosure: steakhousePrimeDisclosure
    },

    // Steakhouse High Yield USDC
    '0x1445A01a57D7B7663CfD7B4EE0a8Ec03B379aabD': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Re7 AUSD
    '0xdc97cF490b2D367F34E5AF8a5f90d2b8FCBA8ce1': {
      disclosure: re7Disclosure
    },

    // Re7 frxUSD
    '0xd80e3811D84Ee7196a187dB3f7bC0B17930730CF': {
      disclosure: re7Disclosure
    },

    // Re7 uSOL
    '0x6680D2993fAadC9204Bd614a53e0c7a3f20c8ca5': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.EtherlinkMainnet]: undefined,
  [ChainId.TacMainnet]: undefined,
  [ChainId.LiskMainnet]: undefined,
  [ChainId.HyperliquidMainnet]: {
    // Gauntlet uETH Vault
    '0x0571362ba5EA9784a97605f57483f865A37dBEAA': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC
    '0x08C00F8279dFF5B0CB5a04d349E7d79708Ceadf3': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDC V2
    '0x9FA2074E43ef6F6dB4a1bB5eeB72e4bc8558bFDe': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDe Core
    '0x5eEC795d919FA97688Fb9844eeB0072E6B846F9d': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet USDT0 Vault
    '0x53A333e51E96FE288bC9aDd7cdC4B1EAD2CD2FfA': {
      disclosure: gauntletDisclosure
    },

    // Gauntlet WHYPE
    '0x264a06Fd7A7C9E0Bfe75163b475E2A3cc1856578': {
      disclosure: gauntletDisclosure
    },

    // Re7 HYPE
    '0x182b318A8F1c7C92a7884e469442a610B0e69ed2': {
      disclosure: re7Disclosure
    },

    // Re7 USDT0
    '0x51F64488d03D8B210294dA2BF70D5db0Bc621B0c': {
      disclosure: re7Disclosure
    }
  },
  [ChainId.MonadMainnet]: {
    // Clearstar Accountable AUSD
    '0xbe3E6d3F857812B4731677C88288D4F3afF8E9a1': {
      disclosure: clearstarAccountableDisclosure
    },

    // Steakhouse High Yield AUSD
    '0xBC03E505EE65f9fAa68a2D7e5A74452858C16D29': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield AUSD V2
    '0xbeeffeA75cFC4128ebe10C8D7aE22016D215060D': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDT0
    '0x961a59Fe249b9795FAE7fA35f9E89629689D5278': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDT0 V2
    '0xbeeff300E9A9caeC7beEA740ab8758D33b777509': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDC
    '0x802c91d807A8DaCA257c4708ab264B6520964e44': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Steakhouse High Yield USDC V2
    '0xbeEFf443C3CbA3E369DA795002243BeaC311aB83': {
      disclosure: steakhouseHighYieldDisclosure
    },

    // Grove x Steakhouse High Yield AUSD
    '0x32841A8511D5c2c5b253f45668780B99139e476D': {
      disclosure: steakhouseHighYieldDisclosure
    }
  },
  [ChainId.SeiMainnet]: undefined,
  [ChainId.ZeroGMainnet]: undefined,
  [ChainId.LineaMainnet]: undefined,
  [ChainId.StableMainnet]: {
    // StableEarn
    '0xb7Df8db22A5DBBFA9ebeb94b3910aec6a4f05c08': {
      disclosure: gauntletDisclosure
    }
  },
  [ChainId.CronosMainnet]: undefined,
  [ChainId.CeloMainnet]: undefined,
  [ChainId.AbstractMainnet]: undefined,
  [ChainId.BitlayerMainnet]: undefined,
  [ChainId.SoneiumMainnet]: undefined,
  [ChainId.BscMainnet]: undefined,
  [ChainId.TempoMainnet]: {
    // Gauntlet pathUSD Frontier V2
    '0xe5235da8Ad839dd2A9De1f1069c89cA3575b5208': {
      disclosure: gauntletDisclosure
    },

    // Tempo Earn
    '0xC609656Ed9ef219c98C8e549bF729144F211f06E': {
      disclosure: gauntletDisclosure
    }
  },
  [ChainId.EdenMainnet]: undefined,
  [ChainId.PharosMainnet]: undefined,
  [ChainId.GensynMainnet]: undefined,
  [ChainId.FlareMainnet]: undefined,
  [ChainId.XdcMainnet]: undefined,
  [ChainId.KaiaMainnet]: undefined,
  [ChainId.RobinhoodMainnet]: {
    // Galaxy USDG Robinhood Stock Tokens
    '0xd0dACE70434fF7567b108B5a8c2a36C150C830FE': {
      disclosure: galaxyRobinhoodStockTokensDisclosure
    }
  },
  [ChainId.ArcMainnet]: {
    // Galaxy EURC
    '0x389abDf4355e0cF4f19298179991705a98f21c18': {
      disclosure: galaxyCircleGuardedDisclosure
    },

    // Galaxy USDC
    '0x8E357432CC12ff425c36432F312968aEb16112AF': {
      disclosure: galaxyCircleGuardedDisclosure
    },

    // Gauntlet EURC Prime
    '0x05863F54B05e96092069eF30c9Ca6060336e50B9': {
      disclosure: gauntletCircleGuardedDisclosure
    },

    // Gauntlet USDC Prime
    '0xdECcd53BE5453215821184824B519E04C7e00bC7': {
      disclosure: gauntletCircleGuardedDisclosure
    }
  },
  [ChainId.MorphMainnet]: undefined,
  [ChainId.MegaEthMainnet]: undefined
}

/**
 * Gets risk disclosures for a given chain ID and address
 * @param chainId - The chain ID to check
 * @param address - The vault/market address to check
 * @returns The risk disclosures if they exist, undefined otherwise
 */
export const getRiskDisclosure = (
  chainId: ChainId,
  address: Address | undefined
): RiskDisclosure | undefined => {
  if (!address) return undefined
  return riskDisclosures[chainId]?.[address]
}
